<template>
    <div class="fixed inset-0 overflow-hidden pointer-events-none">
        <!-- Cerchi pulsanti che rappresentano i log -->
        <template v-for="(_, index) in 10" :key="`pulse-${index}`">
            <div 
                class="log-pulse absolute rounded-full"
                :class="[
                    randomSize(),
                    randomColor(),
                    `animation-delay-${index * 500}`
                ]"
                :style="{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                }"
            ></div>
        </template>

        <!-- Elementi fluttuanti -->
        <template v-for="(_, index) in 10" :key="`float-${index}`">
            <div 
                class="float-element absolute rounded-lg"
                :class="[
                    randomFloatSize(),
                    randomGradient(),
                    `animation-delay-${index * 700}`
                ]"
                :style="{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animationDuration: `${6 + Math.random() * 4}s`
                }"
            ></div>
        </template>

        <!-- Elementi rotanti -->
        <template v-for="(_, index) in 10" :key="`rotate-${index}`">
            <div 
                class="rotate-element absolute rounded-full border"
                :class="[
                    randomRotateSize(),
                    randomBorder(),
                    `animation-delay-${index * 1000}`
                ]"
                :style="{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animationDuration: `${10 + Math.random() * 10}s`,
                    transform: `rotate(${Math.random() * 360}deg)`
                }"
            ></div>
        </template>

        <!-- Particelle -->
        <template v-for="(_, index) in 20" :key="`particle-${index}`">
            <div 
                class="particle absolute rounded-full"
                :class="[
                    randomParticleColor(),
                    `animation-delay-${index * 300}`
                ]"
                :style="{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    width: `${2 + Math.random() * 2}px`,
                    height: `${2 + Math.random() * 2}px`,
                    animationDuration: `${5 + Math.random() * 5}s`
                }"
            ></div>
        </template>

        <!-- Linee di connessione animate -->
        <template v-for="(_, index) in 15" :key="`stream-${index}`">
            <div 
                class="data-stream absolute h-px"
                :class="[
                    randomStreamGradient(),
                    `animation-delay-${index * 600}`
                ]"
                :style="{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    width: `${30 + Math.random() * 50}px`,
                    transform: `rotate(${Math.random() * 360}deg)`
                }"
            ></div>
        </template>

        <!-- Effetti Glitch -->
        <template v-for="(text, index) in ['LOG', 'DATA', 'SYNC']" :key="`glitch-${index}`">
            <div 
                class="glitch-effect absolute font-mono opacity-20"
                :class="[
                    randomGlitchColor(),
                    randomGlitchSize()
                ]"
                :style="{
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    transform: 'translate(-50%, -50%)'
                }"
            >{{ text }}</div>
        </template>
        <!-- Pattern di sfondo -->
        <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMSIgZmlsbD0icmdiYSg2MCw2MCw2MCwwLjEpIi8+PC9zdmc+')] opacity-20"></div>

    </div>
</template>

<script setup>
const randomSize = () => {
    const sizes = ['h-3 w-3', 'h-4 w-4', 'h-5 w-5', 'h-6 w-6'];
    return sizes[Math.floor(Math.random() * sizes.length)];
};

const randomColor = () => {
    const colors = [
        'bg-blue-500/20',
        'bg-purple-500/20',
        'bg-indigo-500/20',
        'bg-pink-500/20',
        'bg-cyan-500/20'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
};

const randomFloatSize = () => {
    const sizes = ['w-6 h-6', 'w-8 h-8', 'w-10 h-10', 'w-12 h-12'];
    return sizes[Math.floor(Math.random() * sizes.length)];
};

const randomGradient = () => {
    const gradients = [
        'bg-gradient-to-r from-blue-500/10 to-purple-500/10',
        'bg-gradient-to-r from-purple-500/10 to-pink-500/10',
        'bg-gradient-to-r from-indigo-500/10 to-blue-500/10',
        'bg-gradient-to-r from-cyan-500/10 to-blue-500/10'
    ];
    return gradients[Math.floor(Math.random() * gradients.length)];
};

const randomRotateSize = () => {
    const sizes = ['w-24 h-24', 'w-32 h-32', 'w-40 h-40'];
    return sizes[Math.floor(Math.random() * sizes.length)];
};

const randomBorder = () => {
    const borders = [
        'border-blue-500/10',
        'border-purple-500/10',
        'border-indigo-500/10',
        'border-pink-500/10'
    ];
    return borders[Math.floor(Math.random() * borders.length)];
};

const randomParticleColor = () => {
    const colors = [
        'bg-blue-400/20',
        'bg-purple-400/20',
        'bg-indigo-400/20',
        'bg-pink-400/20',
        'bg-cyan-400/20'
    ];
    return colors[Math.floor(Math.random() * colors.length)];
};

const randomStreamGradient = () => {
    const gradients = [
        'bg-gradient-to-r from-blue-500/0 via-blue-500/20 to-blue-500/0',
        'bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-purple-500/0',
        'bg-gradient-to-r from-indigo-500/0 via-indigo-500/20 to-indigo-500/0',
        'bg-gradient-to-r from-cyan-500/0 via-cyan-500/20 to-cyan-500/0'
    ];
    return gradients[Math.floor(Math.random() * gradients.length)];
};

const randomGlitchColor = () => {
    const colors = ['text-blue-500', 'text-purple-500', 'text-indigo-500', 'text-cyan-500'];
    return colors[Math.floor(Math.random() * colors.length)];
};

const randomGlitchSize = () => {
    const sizes = ['text-2xl', 'text-3xl', 'text-4xl'];
    return sizes[Math.floor(Math.random() * sizes.length)];
};
</script>

<style scoped>
/* Animazione pulsante per i log */
@keyframes log-pulse {
    0% {
        transform: scale(1) rotate(0deg);
        opacity: 0.5;
    }
    50% {
        transform: scale(1.5) rotate(180deg);
        opacity: 0.2;
    }
    100% {
        transform: scale(1) rotate(360deg);
        opacity: 0.5;
    }
}

.log-pulse {
    animation: log-pulse 3s ease-in-out infinite;
}

/* Animazione float con movimento casuale */
@keyframes float {
    0%, 100% {
        transform: translate(0, 0) rotate(0deg);
    }
    25% {
        transform: translate(10px, -10px) rotate(5deg);
    }
    50% {
        transform: translate(-5px, -20px) rotate(-5deg);
    }
    75% {
        transform: translate(-10px, -10px) rotate(3deg);
    }
}

.float-element {
    animation: float 6s ease-in-out infinite;
}

/* Animazione rotate con scala */
@keyframes rotate {
    0% {
        transform: rotate(0deg) scale(1);
    }
    50% {
        transform: rotate(180deg) scale(1.1);
    }
    100% {
        transform: rotate(360deg) scale(1);
    }
}

.rotate-element {
    animation: rotate 10s linear infinite;
}

/* Animazione particelle con movimento casuale */
@keyframes particle-move {
    0%, 100% {
        transform: translate(0, 0);
    }
    25% {
        transform: translate(20px, -15px);
    }
    50% {
        transform: translate(-15px, -25px);
    }
    75% {
        transform: translate(-20px, 10px);
    }
}

.particle {
    animation: particle-move 8s ease-in-out infinite;
}

/* Animazione stream di dati */
@keyframes data-stream {
    0% {
        transform: translateX(-100%) var(--rotation);
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    100% {
        transform: translateX(100%) var(--rotation);
        opacity: 0;
    }
}

.data-stream {
    animation: data-stream 4s linear infinite;
    --rotation: rotate(45deg);
}

/* Effetto glitch migliorato */
@keyframes glitch {
    0% {
        transform: translate(-50%, -50%) skew(0deg);
    }
    20% {
        transform: translate(-52%, -48%) skew(4deg);
    }
    40% {
        transform: translate(-48%, -52%) skew(-4deg);
    }
    60% {
        transform: translate(-51%, -49%) skew(2deg);
    }
    80% {
        transform: translate(-49%, -51%) skew(-2deg);
    }
    100% {
        transform: translate(-50%, -50%) skew(0deg);
    }
}

.glitch-effect {
    animation: glitch 0.5s ease-in-out infinite;
    animation-play-state: paused;
}

.glitch-effect:hover {
    animation-play-state: running;
}

/* Delay per le animazioni */
.animation-delay-300 { animation-delay: 300ms; }
.animation-delay-500 { animation-delay: 500ms; }
.animation-delay-600 { animation-delay: 600ms; }
.animation-delay-700 { animation-delay: 700ms; }
.animation-delay-1000 { animation-delay: 1000ms; }
.animation-delay-1500 { animation-delay: 1500ms; }
.animation-delay-2000 { animation-delay: 2000ms; }
.animation-delay-2500 { animation-delay: 2500ms; }
.animation-delay-3000 { animation-delay: 3000ms; }
</style> 